/**
 * Cross-sell completion toast detector.
 *
 * Subscribes to the queue store and fires a proactive Ant `notification` (+
 * optional desktop Notification when the tab is hidden) exactly once per job
 * when it transitions from `processing` to a terminal state (UX_SPEC_F1_DELTA
 * §4.5). Mounted once in AppLayout alongside the global stream hook.
 */
import { useEffect } from 'react';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useCrossSellQueueStore,
  type CrossSellQueueEntry,
} from '../stores/useCrossSellQueueStore';

const NOTIFICATION_ICON = '/favicon.svg';

function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return Promise.resolve('denied');
  }
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Promise.resolve(Notification.permission);
  }
  try {
    return Notification.requestPermission();
  } catch {
    return Promise.resolve('denied');
  }
}

export function useCrossSellToastDetector(): void {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Cap simultaneous toasts (platform convention).
    notification.config({ maxCount: 3 });

    const buildDescription = (entry: CrossSellQueueEntry): string => {
      if (entry.status !== 'completed') return entry.label;
      return t('crossSell.import.done.summary', {
        accounts: entry.accountsImported ?? 0,
        assets: entry.assetsImported ?? 0,
        decisores: entry.decisoresImported ?? 0,
      });
    };

    const fireDesktop = (entry: CrossSellQueueEntry) => {
      if (!document.hidden) return;
      void ensureNotificationPermission().then((perm) => {
        if (perm !== 'granted') return;
        try {
          const title =
            entry.status === 'completed'
              ? t('crossSell.import.done.completedTitle')
              : t('crossSell.import.done.failedTitle');
          const n = new Notification(title, {
            body: buildDescription(entry),
            icon: NOTIFICATION_ICON,
          });
          void n;
        } catch {
          /* best-effort */
        }
      });
    };

    const unsub = useCrossSellQueueStore.subscribe((state, prev) => {
      state.entries.forEach((entry) => {
        const wasProcessing =
          prev.entries.find((e) => e.jobId === entry.jobId)?.status === 'processing';
        const isTerminal = entry.status !== 'processing';
        if (!wasProcessing || !isTerminal || entry.alertEmitted) return;

        notification.open({
          key: `cross-sell-${entry.jobId}`,
          message:
            entry.status === 'completed'
              ? t('crossSell.import.done.completedTitle')
              : t('crossSell.import.done.failedTitle'),
          description: buildDescription(entry),
          // Error toasts don't auto-close; success closes after 6s.
          duration: entry.status === 'completed' ? 6 : 0,
          onClick: () => navigate(`/cross-sell/import/${entry.jobId}`),
        });
        fireDesktop(entry);
        useCrossSellQueueStore.getState().markAlertEmitted(entry.jobId);
      });
    });

    return unsub;
  }, [navigate, t]);
}
